import HttpCliente from '../HttpCliente';

/**
 * GET method - Obtiene todos los livestreams
 * @param {Function} setLivestreams función para establecer la lista de livestreams
 * @param {Function} setError función para establecer el estado de error
 * @param {boolean} includeDisabled indica si se deben incluir livestreams deshabilitados
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getAllLivestreams = (setLivestreams, setError, includeDisabled = false, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream?includeDisabled=${includeDisabled}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestreams(response.data);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            if (error.response && error.response.data) {
                setError(error.response.data.message);
                reject(error.response.data.message);
            } else {
                setError("Error de conexión");
                reject("Error de conexión");
            }
        });
    });
};

/**
 * GET method - Obtiene livestreams por estado (Próximo, En Vivo, Finalizado)
 * @param {string} status estado de los livestreams a obtener
 * @param {Function} setLivestreams función para establecer la lista de livestreams
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getLivestreamsByStatus = (status, setLivestreams, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream/status/${status}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestreams(response.data);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * GET method - Obtiene información de un livestream por ID
 * @param {string} id ID del livestream
 * @param {Function} setLivestream función para establecer el livestream
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getLivestreamById = (id, setLivestream, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream/${id}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestream(response.data);
                resolve(response);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * POST method - Crea un nuevo livestream (solo admin)
 * @param {Object} livestreamData datos del livestream a crear
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de creación
 */
export const createLivestream = (livestreamData, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.post('/Livestream', livestreamData, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*',
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (response.status === 201) {
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * PUT method - Actualiza un livestream existente (solo admin)
 * @param {string} id ID del livestream a actualizar
 * @param {Object} livestreamData nuevos datos del livestream
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de actualización
 */
export const updateLivestream = (id, livestreamData, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.put(`/Livestream/${id}`, livestreamData, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*',
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            if (response.status === 204) {
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * PATCH method - Alterna el estado de habilitado de un livestream (solo admin)
 * @param {string} id ID del livestream
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de alternancia
 */
export const toggleLivestreamStatus = (id, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.patch(`/Livestream/${id}/toggle-status`, {}, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 204) {
                resolve();
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * DELETE method - Elimina un livestream (solo admin)
 * @param {string} id ID del livestream a eliminar
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de eliminación
 */
export const deleteLivestream = (id, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.delete(`/Livestream/${id}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 204) {
                resolve();
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * GET method - Verifica si el usuario tiene acceso a un livestream
 * @param {string} id ID del livestream
 * @param {Function} setHasAccess función para establecer si tiene acceso
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de verificación
 */
export const checkLivestreamAccess = (id, setHasAccess, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream/${id}/check-access`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setHasAccess(response.data.hasAccess);
                resolve(response.data.hasAccess);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * POST method - Otorga acceso al usuario actual para un livestream
 * @param {string} id ID del livestream
 * @param {Function} setAccessCode función para establecer el código de acceso
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de otorgamiento
 */
export const grantLivestreamAccess = (id, setAccessCode, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.post(`/Livestream/${id}/grant-access`, {}, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setAccessCode(response.data.codigoAcceso);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * POST method - Valida un código de acceso para un livestream
 * @param {string} id ID del livestream
 * @param {string} accessCode código de acceso a validar
 * @param {Function} setIsValid función para establecer si el código es válido
 * @param {Function} setError función para establecer el estado de error
 * @return {Promise} Promesa de validación
 */
export const validateAccessCode = (id, accessCode, setIsValid, setError) => {
    return new Promise((resolve, reject) => {
        HttpCliente.post(`/Livestream/${id}/validate-access`, { codigoAcceso: accessCode }, {
            headers: {
                'Content-Type': 'application/json',
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setIsValid(response.data.isValid);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * POST method - Actualiza el contador de vistas de un livestream
 * @param {string} id ID del livestream
 * @param {Function} setError función para establecer el estado de error
 * @return {Promise} Promesa de actualización
 */
export const updateViewCount = (id, setError) => {
    return new Promise((resolve, reject) => {
        HttpCliente.post(`/Livestream/${id}/update-views`, {}, {
            headers: {
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 204) {
                resolve();
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * GET method - Obtiene livestreams por comediante
 * @param {string} comedianId ID del comediante
 * @param {Function} setLivestreams función para establecer la lista de livestreams
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getLivestreamsByComedian = (comedianId, setLivestreams, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream/comedian/${comedianId}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestreams(response.data);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * GET method - Obtiene livestreams por tag
 * @param {string} tagId ID del tag
 * @param {Function} setLivestreams función para establecer la lista de livestreams
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getLivestreamsByTag = (tagId, setLivestreams, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get(`/Livestream/tag/${tagId}`, {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestreams(response.data);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};

/**
 * GET method - Obtiene los livestreams accesibles para el usuario actual
 * @param {Function} setLivestreams función para establecer la lista de livestreams
 * @param {Function} setError función para establecer el estado de error
 * @param {string} token token de autenticación
 * @return {Promise} Promesa de obtención
 */
export const getUserLivestreams = (setLivestreams, setError, token) => {
    return new Promise((resolve, reject) => {
        HttpCliente.get('/Livestream/user', {
            headers: {
                "Authorization": `Bearer ${token}`,
                'accept': '*/*'
            }
        })
        .then(response => {
            if (response.status === 200) {
                setLivestreams(response.data);
                resolve(response.data);
            } else {
                setError(response.data.message);
                reject(response.data.message);
            }
        })
        .catch(error => {
            const errorMessage = error.response?.data?.message || "Error de conexión";
            setError(errorMessage);
            reject(errorMessage);
        });
    });
};